import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogActions,
} from '@mui/material';

import {
  getEventRuntime,
  startEvent,
  endEvent,
  adjustScore,
  resetGame,
} from '../../services/event-runtime.service';

type Runtime = {
  eventId: string;
  name: string;
  title?: string;
  gameType: string;
  phase?: 'pre' | 'during' | 'post';
  gamePeriod: number;
  maxGamePeriods: number;
  inPlay: boolean;
  periodStatus?: 'ready' | 'running' | 'ended';
  labels?: {
    currentPeriodName: string;
    nextPeriodName: string;
    startActionLabel: string;
    endActionLabel: string;
    gameOverLabel: string;
    continuous?: boolean;
  };
  score?: {
    left: number;
    right: number;
  };
  timer: {
    startedAt: number | null;
    accumulatedMs: number;
    running: boolean;
  };
  permissions: {
    canControlTimer: boolean;
    canViewTimer: boolean;
  };
  log: { ts: number; action: string; note?: string }[];
};

function toTitleCase(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function splitTeamsFromTitle(title: string): { leftTeam: string; rightTeam: string } | null {
  const parts = title.split(/\s+vs\s+/i);

  if (parts.length !== 2) {
    return null;
  }

  return {
    leftTeam: parts[0].trim(),
    rightTeam: parts[1].trim(),
  };
}

function isRuntimeShape(value: any): value is Runtime {
  return !!value &&
    typeof value === 'object' &&
    typeof value.eventId === 'string' &&
    typeof value.name === 'string' &&
    typeof value.gameType === 'string' &&
    typeof value.gamePeriod === 'number' &&
    typeof value.maxGamePeriods === 'number' &&
    typeof value.inPlay === 'boolean' &&
    !!value.timer &&
    typeof value.timer === 'object' &&
    typeof value.timer.accumulatedMs === 'number' &&
    typeof value.timer.running === 'boolean' &&
    !!value.permissions &&
    typeof value.permissions === 'object' &&
    typeof value.permissions.canControlTimer === 'boolean' &&
    typeof value.permissions.canViewTimer === 'boolean' &&
    Array.isArray(value.log);
}

export default function EventServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [runtime, setRuntime] = useState<Runtime | null>(null);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [confirm, setConfirm] = useState<'start' | 'end' | 'reset' | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState('');
  const [scoreBusy, setScoreBusy] = useState<'left-add' | 'left-sub' | 'right-add' | 'right-sub' | null>(null);

  const logPrefix = useMemo(() => `[event-service id=${String(id || '')}]`, [id]);

  const loadRuntime = useCallback(async (silent = false) => {
    if (!id) {
      console.warn(`${logPrefix} loadRuntime skipped because route param id is missing`);
      setErrorText('Missing event id in route');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      const data = await getEventRuntime(id);

      if (!isRuntimeShape(data)) {
        setErrorText('Runtime response shape invalid');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to load event runtime';

      setErrorText(String(message));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id, logPrefix]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setErrorText('Missing event id in route');
      return;
    }

    loadRuntime();

    const interval = setInterval(() => {
      loadRuntime(true);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [id, loadRuntime]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!runtime) return;

      const { startedAt, accumulatedMs, running } = runtime.timer;

      let total = accumulatedMs;

      if (running && startedAt) {
        total += Date.now() - startedAt;
      }

      setDisplaySeconds(Math.floor(total / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [runtime]);

  useEffect(() => {
    if (!runtime) return;

    const { startedAt, accumulatedMs, running } = runtime.timer;

    let total = accumulatedMs;

    if (running && startedAt) {
      total += Date.now() - startedAt;
    }

    setDisplaySeconds(Math.floor(total / 1000));
  }, [runtime]);

  if (loading && !runtime) {
    return (
      <Box p={2}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!runtime) {
    return (
      <Box p={2}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorText || 'Runtime is empty on the client'}
        </Typography>

        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  const minutes = Math.floor(displaySeconds / 60);
  const seconds = displaySeconds % 60;
  const isAdmin = runtime.permissions.canControlTimer;

  const phase =
    runtime.phase ||
    (runtime.gamePeriod === 0 && !runtime.inPlay
      ? 'pre'
      : runtime.gamePeriod >= runtime.maxGamePeriods && !runtime.inPlay
      ? 'post'
      : 'during');

  const phaseLabel = phase === 'pre' ? 'Pre-game' : phase === 'post' ? 'Post-game' : 'During';

  const currentPeriodName = runtime.labels?.currentPeriodName || 'Period';

  const nextPeriodNumber =
    runtime.gamePeriod === 0
      ? 1
      : Math.min(runtime.gamePeriod + 1, runtime.maxGamePeriods);

  const nextPeriodName =
    runtime.labels?.nextPeriodName || `Period ${nextPeriodNumber}`;

  const scoreLeft = runtime.score?.left ?? 0;
  const scoreRight = runtime.score?.right ?? 0;

  const teams = splitTeamsFromTitle(runtime.name || '');
  const leftTeamName = teams?.leftTeam || 'Team A';
  const rightTeamName = teams?.rightTeam || 'Team B';

  const isHalfTime =
    phase === 'during' &&
    !runtime.inPlay &&
    runtime.gamePeriod > 0 &&
    runtime.gamePeriod < runtime.maxGamePeriods;

  const isFullTime = phase === 'post';

  const scoreTitle = teams
    ? `${leftTeamName} ${scoreLeft} - ${scoreRight} ${rightTeamName}`
    : `${runtime.name} ${scoreLeft} - ${scoreRight}`;

  const titleText = phase === 'pre'
    ? runtime.name
    : isHalfTime
    ? `HT: ${scoreTitle}`
    : isFullTime
    ? `FT: ${scoreTitle}`
    : scoreTitle;

  const periodSummary = runtime.labels?.continuous
    ? currentPeriodName
    : `${currentPeriodName}`;

  const primaryAction: 'start' | 'end' | null =
    phase === 'post'
      ? null
      : runtime.inPlay
      ? 'end'
      : 'start';

  const primaryActionLabel =
    primaryAction === 'start'
      ? runtime.labels?.startActionLabel || `Start ${runtime.gamePeriod === 0 ? currentPeriodName : nextPeriodName}`
      : primaryAction === 'end'
      ? runtime.labels?.endActionLabel || 'End Period'
      : runtime.labels?.gameOverLabel || 'Game Over';

  const handleScore = async (
    side: 'left' | 'right',
    delta: number,
    busyKey: 'left-add' | 'left-sub' | 'right-add' | 'right-sub',
  ) => {
    if (!id) return;

    try {
      setScoreBusy(busyKey);
      const data = await adjustScore(id, side, delta);

      if (!isRuntimeShape(data)) {
        setErrorText('Score action returned invalid runtime shape');
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Score action failed';
      setErrorText(String(message));
    } finally {
      setScoreBusy(null);
    }
  };

  const handleConfirmedAction = async () => {
    if (!id || !confirm) {
      return;
    }

    try {
      const data =
        confirm === 'start'
          ? await startEvent(id)
          : confirm === 'end'
          ? await endEvent(id)
          : await resetGame(id);

      if (!isRuntimeShape(data)) {
        setErrorText(`${confirm} action returned invalid runtime shape`);
        return;
      }

      setRuntime(data);
      setErrorText('');
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Action failed';

      setErrorText(String(message));
    } finally {
      setConfirm(null);
    }
  };

  const confirmTitle =
    confirm === 'start'
      ? `${primaryActionLabel}?`
      : confirm === 'end'
      ? `${primaryActionLabel}?`
      : 'Reset game?';

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f5f7">
      <Box
        width={{ xs: 220, md: 280 }}
        bgcolor="#eef1f5"
        borderRight="1px solid #d8dde6"
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        p={2}
        gap={2}
      >
        {isAdmin && primaryAction && (
          <Button
            variant="contained"
            color={primaryAction === 'start' ? 'success' : 'error'}
            sx={{ py: 1.5 }}
            onClick={() => setConfirm(primaryAction)}
          >
            {primaryActionLabel}
          </Button>
        )}

        {isAdmin && (
          <Button
            variant="outlined"
            color="warning"
            sx={{ py: 1.5 }}
            onClick={() => setConfirm('reset')}
          >
            Reset Game
          </Button>
        )}

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Score
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {scoreLeft} - {scoreRight}
          </Typography>

          {isAdmin ? (
            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                fullWidth
                variant="contained"
                disabled={scoreBusy !== null}
                onClick={() => handleScore('left', 1, 'left-add')}
              >
                {leftTeamName} +
              </Button>

              <Button
                fullWidth
                variant="contained"
                disabled={scoreBusy !== null}
                onClick={() => handleScore('right', 1, 'right-add')}
              >
                {rightTeamName} +
              </Button>

              <Button
                fullWidth
                variant="outlined"
                disabled={scoreBusy !== null}
                onClick={() => handleScore('left', -1, 'left-sub')}
              >
                {leftTeamName} -
              </Button>

              <Button
                fullWidth
                variant="outlined"
                disabled={scoreBusy !== null}
                onClick={() => handleScore('right', -1, 'right-sub')}
              >
                {rightTeamName} -
              </Button>
            </Box>
          ) : (
            <Typography variant="body2">Score controls are limited to timer controllers.</Typography>
          )}
        </Paper>

        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Box>

      <Box flex={1} p={2} overflow="auto">
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Box minWidth={260}>
              <Typography variant="h6">{titleText}</Typography>
              <Typography variant="body2">{toTitleCase(runtime.gameType)}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {phaseLabel}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {runtime.inPlay ? 'In Play' : phase === 'post' ? 'Game Over' : 'Stopped'}
              </Typography>
              {!!errorText && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  {errorText}
                </Typography>
              )}
            </Box>

            <Box textAlign={{ xs: 'left', md: 'right' }} minWidth={180}>
              <Typography variant="h2" sx={{ lineHeight: 1 }}>
                {minutes}:{seconds.toString().padStart(2, '0')}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                {periodSummary}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Refreshing every 5 seconds
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recent Actions
          </Typography>

          {runtime.log.length === 0 ? (
            <Typography variant="body2">No actions yet</Typography>
          ) : (
            runtime.log
              .slice(-5)
              .reverse()
              .map((l, i) => (
                <Typography key={i} variant="body2">
                  {new Date(l.ts).toLocaleTimeString()} — {l.action}
                </Typography>
              ))
          )}
        </Paper>
      </Box>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>{confirmTitle}</DialogTitle>

        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button onClick={handleConfirmedAction}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}